import React from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// overallInsight() and categoryInsight() — local fallbacks when backend data is absent.
// Backend (PlayReportManager.GetSessionSummary) is the primary source.
function overallInsight(summary, wellness) {
  const deltas = summary?.categoryDeltas || summary?.CategoryDeltas || [];
  if (deltas.length === 0) return null;
  const improving = deltas.filter((d) => (d.delta ?? d.Delta ?? 0) > 0).length;
  const declining = deltas.filter((d) => (d.delta ?? d.Delta ?? 0) < 0).length;
  const streak = summary?.streakDays ?? summary?.StreakDays ?? 0;
  const scores = deltas.map((d) => d.latestScore ?? d.LatestScore ?? 0);
  const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
  let headline, detail;
  if (avg >= 8) {
    headline = "Outstanding performance this week";
    detail = `${improving} areas improving. You're in great shape — keep it up!`;
  } else if (avg >= 6.5) {
    headline = wellness ? "Great progress this week" : "Strong cognitive health";
    detail = wellness
      ? `${improving} areas improving, ${declining} with room to grow. Stay consistent!`
      : `${improving} categories improving, ${declining} declining. Keep up the consistency.`;
  } else if (avg >= 5) {
    headline = wellness ? "Good effort — keep practising" : "Moderate cognitive performance";
    detail = wellness
      ? `You have ${declining} areas to focus on this week. More play time helps!`
      : `${declining} categories need attention. Focus on lower-scoring areas.`;
  } else {
    headline = wellness ? "You have areas to focus on this week" : "Cognitive health needs attention";
    detail = wellness
      ? "Some scores dipped this week. Daily practice makes a big difference — you've got this!"
      : "Scores are below target across multiple areas. Daily practice makes a significant difference.";
  }
  const streakText = streak > 0 ? `You're on a ${streak}-day streak — keep it going!` : "";
  return { Headline: headline, Detail: detail, TrendText: "", StreakText: streakText };
}

function categoryInsight(item, wellness) {
  const score = item.latestScore ?? item.LatestScore ?? 0;
  const delta = item.delta ?? item.Delta ?? 0;
  const category = item.category || item.Category || "";
  const deltaText = delta > 0.05 ? `↑ +${delta.toFixed(1)} vs last period`
    : delta < -0.05 ? `↓ ${delta.toFixed(1)} vs last period`
    : "→ Stable vs last period";
  let tipText = "";
  if (score >= 8) tipText = `${category} is excellent. Maintain your current routine.`;
  else if (score >= 6.5) tipText = `${category} is good. Small increases in practice will push you higher.`;
  else if (score >= 5) tipText = wellness
    ? `${category} has room to grow — try adding one extra session per day.`
    : `${category} is moderate. Try adding one extra session per day.`;
  else tipText = wellness
    ? `${category} score dipped this week — prioritise these games in your routine.`
    : `${category} needs work. Prioritise these exercises in your daily routine.`;
  return { tipText, deltaText };
}

const Tips = ({ sessionSummary, loading, playPct, wellness, categoryScores }) => {
  const summary = sessionSummary;

  const deltaColor = (delta) => {
    if (delta > 0) return "success";
    if (delta < 0) return "error";
    return "text";
  };

  const deltaIcon = (delta) => {
    if (delta > 0) return "trending_up";
    if (delta < 0) return "trending_down";
    return "trending_flat";
  };

  // Backend insight takes priority; fall back to local overallInsight()
  const insight = summary
    ? (summary.overallInsight || summary.OverallInsight || overallInsight(summary, wellness))
    : null;

  return (
    <Card sx={{ width: "100%" }}>
      <MDBox display="flex">
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          variant="gradient"
          bgColor="info"
          color="white"
          shadow="md"
          borderRadius="xl"
          ml={3}
          mt={-2}
        >
          <Icon fontSize="medium" color="inherit">insights</Icon>
        </MDBox>
        <MDBox>
          <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
            {wellness ? "Wellness Progress Summary" : "Cognitive Progress Summary"}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox px={4} pb={3} mt={1}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </MDBox>
        ) : !summary ? (
          <MDTypography variant="body2" color="secondary" py={2}>
            No session data available yet.
          </MDTypography>
        ) : (
          <>
            {/* Streak + last session */}
            <Grid container spacing={3} mb={2}>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox textAlign="center" py={2}>
                  <MDTypography variant="h3" color="info" fontWeight="bold">
                    {summary.streakDays ?? summary.StreakDays ?? 0}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">Day Streak</MDTypography>
                </MDBox>
              </Grid>
              {playPct != null && (
                <Grid item xs={12} sm={6} md={3}>
                  <MDBox textAlign="center" py={2}>
                    <MDTypography variant="h3" color="success" fontWeight="bold">
                      {playPct}%
                    </MDTypography>
                    <MDTypography variant="caption" color="text">Play Report</MDTypography>
                  </MDBox>
                </Grid>
              )}

              {(summary.lastSessionDate || summary.LastSessionDate) && (
                <Grid item xs={12} sm={6} md={4}>
                  <MDBox py={2} display="flex" alignItems="center" gap={1}>
                    <Icon fontSize="small" sx={{ color: "text.secondary" }}>schedule</Icon>
                    <MDTypography variant="body2" color="text">
                      Last session:{" "}
                      <strong>
                        {new Date(summary.lastSessionDate || summary.LastSessionDate).toLocaleDateString("en-GB", {
                          day: "2-digit", month: "short", year: "numeric",
                        })}
                      </strong>
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>

            {/* Overall insight — now from backend */}
            {insight?.headline || insight?.Headline ? (
              <>
                <Divider />
                <MDBox
                  mt={2} mb={2} px={2} py={2} borderRadius="lg"
                  sx={{ background: "rgba(25, 118, 210, 0.06)", borderLeft: "4px solid #1976d2" }}
                >
                  <MDBox display="flex" alignItems="flex-start" gap={1}>
                    <Icon sx={{ color: "#1976d2", mt: 0.3 }}>psychology</Icon>
                    <MDBox>
                      <MDTypography variant="button" fontWeight="bold" display="block">
                        {insight.headline || insight.Headline}
                      </MDTypography>
                      <MDTypography variant="body2" color="text" mt={0.5}>
                        {insight.detail || insight.Detail}
                      </MDTypography>
                      {(insight.trendText || insight.TrendText) && (
                        <MDTypography variant="body2" color="text" mt={0.5}>
                          {insight.trendText || insight.TrendText}
                        </MDTypography>
                      )}
                      {(insight.streakText || insight.StreakText) && (
                        <MDTypography variant="body2" color="text" mt={0.5}>
                          {insight.streakText || insight.StreakText}
                        </MDTypography>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
              </>
            ) : null}

            {/* Per-category breakdown — tipText and deltaText now from backend */}
            {Array.isArray(summary.categoryDeltas || summary.CategoryDeltas) &&
              (summary.categoryDeltas || summary.CategoryDeltas).length > 0 && (
              <>
                <Divider />
                <MDBox mt={2}>
                  <MDTypography variant="caption" color="text" fontWeight="bold"
                    textTransform="uppercase" mb={1} display="block">
                    Category Insights
                  </MDTypography>
                  <Grid container spacing={2} mt={0.5}>
                    {(summary.categoryDeltas || summary.CategoryDeltas).map((item, idx) => {
                      const delta = item.delta ?? item.Delta ?? 0;
                      const rawScore = item.latestScore ?? item.LatestScore ?? 0;
                      const category = item.category || item.Category || "";
                      // Use the period-aggregated score from activitySummary (matches score cards)
                      // rather than the "latest single session" score from sessionSummary.
                      const summaryEntry = (categoryScores || []).find(
                        (s) => (s.category || s.Category || "").toLowerCase() === category.toLowerCase()
                      );
                      const latestScore = summaryEntry
                        ? (summaryEntry.score ?? summaryEntry.Score ?? rawScore)
                        : rawScore;
                      const fallback = categoryInsight(item, wellness);
                      const tipText = item.tipText || item.TipText || fallback.tipText;
                      const deltaText = item.deltaText || item.DeltaText || fallback.deltaText;
                      return (
                        <Grid key={idx} item xs={12} sm={6} md={4}>
                          <MDBox px={2} py={1.5} borderRadius="lg"
                            sx={{ background: "rgba(0,0,0,0.03)", height: "100%" }}>
                            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                              <MDTypography variant="button" fontWeight="bold">
                                {category}
                              </MDTypography>
                              <MDBox display="flex" alignItems="center" gap={0.5}>
                                <MDTypography variant="h6" color={deltaColor(delta)} fontWeight="bold">
                                  {latestScore != null ? latestScore.toFixed(1) : "—"}
                                </MDTypography>
                                <Icon fontSize="small" color={deltaColor(delta)}>
                                  {deltaIcon(delta)}
                                </Icon>
                                <MDTypography variant="caption" color={deltaColor(delta)}>
                                  {delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1)}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            {deltaText && (
                              <MDTypography variant="caption" color={deltaColor(delta)}
                                display="block" mb={0.25} fontWeight="medium">
                                {deltaText}
                              </MDTypography>
                            )}
                            <MDTypography variant="caption" color="text" display="block">
                              {tipText}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      );
                    })}
                  </Grid>
                </MDBox>
              </>
            )}
          </>
        )}
      </MDBox>
    </Card>
  );
};

export default Tips;
